import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { Download, Assessment } from '@mui/icons-material';
import apiService from '../services/apiService';

const ReportsPage = () => {
  const [reportType, setReportType] = useState('rides');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getReport(reportType, {
        startDate,
        endDate,
      });

      if (response.success) {
        // Download report
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}_report_${startDate}_to_${endDate}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert(response.message || 'Failed to generate report');
      }
    } catch (err) {
      alert('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate various platform reports
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Assessment color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Generate Report
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      label="Report Type"
                    >
                      <MenuItem value="rides">Rides Report</MenuItem>
                      <MenuItem value="revenue">Revenue Report</MenuItem>
                      <MenuItem value="drivers">Drivers Report</MenuItem>
                      <MenuItem value="passengers">Passengers Report</MenuItem>
                      <MenuItem value="payments">Payments Report</MenuItem>
                      <MenuItem value="commissions">Commission Report</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<Download />}
                    onClick={handleGenerate}
                    disabled={loading}
                  >
                    Generate & Download Report
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Available Reports
              </Typography>
              <Box sx={{ mt: 2 }}>
                {[
                  { title: 'Rides Report', desc: 'Complete ride statistics and details' },
                  { title: 'Revenue Report', desc: 'Total revenue and earnings breakdown' },
                  { title: 'Drivers Report', desc: 'Driver performance and earnings' },
                  { title: 'Passengers Report', desc: 'Passenger activity and spending' },
                  { title: 'Payments Report', desc: 'Payment transactions and methods' },
                  { title: 'Commission Report', desc: 'Platform commission details' },
                ].map((report, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {report.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {report.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPage;
